/**
 * ZenPacks Theme v2.0 — Premium Eco-Packaging Shopify Theme
 * Copyright © 2026 ZenPacks (zenpacks.com.au)
 *
 * Production-quality JavaScript for a premium Shopify storefront.
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. UTILITY HELPERS
     ========================================================================== */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function debounce(fn, ms = 150) {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  }

  function throttle(fn, ms = 100) {
    let last = 0;
    return (...a) => {
      const now = Date.now();
      if (now - last >= ms) { last = now; fn(...a); }
    };
  }

  function trapFocus(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    el.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ==========================================================================
     2. STICKY HEADER WITH SCROLL DETECTION
     ========================================================================== */

  const StickyHeader = {
    el: null,
    lastScroll: 0,
    threshold: 80,

    init() {
      this.el = $('.site-header');
      if (!this.el) return;

      // Set initial state
      this.update();

      window.addEventListener('scroll', throttle(() => this.update(), 50), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 200), { passive: true });
    },

    update() {
      const y = window.scrollY;
      const isScrolled = y > this.threshold;

      this.el.classList.toggle('site-header--scrolled', isScrolled);
      this.el.classList.toggle('site-header--transparent', !isScrolled);

      // Hide announcement bar on scroll
      const announcement = $('.announcement-bar');
      if (announcement) {
        if (isScrolled) {
          announcement.style.transform = `translateY(-100%)`;
          announcement.style.position = 'fixed';
          announcement.style.top = '0';
          announcement.style.left = '0';
          announcement.style.right = '0';
        } else {
          announcement.style.transform = '';
          announcement.style.position = '';
          announcement.style.top = '';
          announcement.style.left = '';
          announcement.style.right = '';
        }
      }

      this.lastScroll = y;
    }
  };

  /* ==========================================================================
     3. MOBILE HAMBURGER MENU
     ========================================================================== */

  const MobileMenu = {
    isOpen: false,

    init() {
      const toggle = $('.header__mobile-toggle') || $('#mobile-menu-toggle');
      const close = $('.mobile-nav__close') || $('#mobile-nav-close');
      const backdrop = $('.mobile-nav__overlay') || $('#mobile-nav-overlay');

      if (toggle) toggle.addEventListener('click', () => this.toggle());
      if (close) close.addEventListener('click', () => this.close());
      if (backdrop) backdrop.addEventListener('click', () => this.close());

      // Submenu toggles (accordion triggers)
      $$('.mobile-nav__accordion-trigger').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const expanded = link.getAttribute('aria-expanded') === 'true';
          link.setAttribute('aria-expanded', !expanded);
          const submenu = link.nextElementSibling;
          if (submenu) {
            submenu.classList.toggle('is-open');
            link.classList.toggle('is-active');
          }
        });
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    open() {
      this.isOpen = true;
      document.documentElement.classList.add('mobile-menu--open');
      const panel = $('#mobile-nav') || $('.mobile-nav');
      if (panel) {
        panel.classList.add('is-active');
        panel.setAttribute('aria-hidden', 'false');
        trapFocus(panel);
      }
      const overlay = $('#mobile-nav-overlay') || $('.mobile-nav__overlay');
      if (overlay) overlay.classList.add('is-active');
      const toggle = $('#mobile-menu-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.isOpen = false;
      document.documentElement.classList.remove('mobile-menu--open');
      const panel = $('#mobile-nav') || $('.mobile-nav');
      if (panel) {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      }
      const overlay = $('#mobile-nav-overlay') || $('.mobile-nav__overlay');
      if (overlay) overlay.classList.remove('is-active');
      const toggle = $('#mobile-menu-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  };

  /* ==========================================================================
     4. MEGA MENU WITH HOVER INTENT
     ========================================================================== */

  const MegaMenu = {
    timeout: null,
    delay: 200,

    init() {
      // Support both old .nav__item[data-has-dropdown] and new [data-mega-trigger]
      const megaItems = $$('[data-mega-trigger], .header__nav-item--mega, .nav__item[data-has-dropdown]');
      megaItems.forEach(item => {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.timeout);
          // Close other open menus
          $$('.header__nav-item.is-open, .nav__item.is-open').forEach(other => {
            if (other !== item) other.classList.remove('is-open');
          });
          item.classList.add('is-open');
          const megaMenu = item.querySelector('[data-mega-menu], .mega-menu');
          if (megaMenu) megaMenu.setAttribute('aria-hidden', 'false');
        });

        item.addEventListener('mouseleave', () => {
          this.timeout = setTimeout(() => {
            item.classList.remove('is-open');
            const megaMenu = item.querySelector('[data-mega-menu], .mega-menu');
            if (megaMenu) megaMenu.setAttribute('aria-hidden', 'true');
          }, this.delay);
        });

        // Keyboard accessibility
        const link = item.querySelector('.header__nav-link, .nav__link');
        if (link) {
          link.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              item.classList.toggle('is-open');
            }
          });
        }
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.header__nav-item') && !e.target.closest('.nav__item')) {
          $$('.header__nav-item.is-open, .nav__item.is-open').forEach(item => item.classList.remove('is-open'));
        }
      });
    }
  };

  /* ==========================================================================
     5. PRODUCT IMAGE GALLERY
     ========================================================================== */

  const ProductGallery = {
    mainImage: null,
    thumbs: [],
    currentIndex: 0,

    init() {
      this.mainImage = $('.product-gallery__main img');
      this.thumbs = $$('.product-gallery__thumb');

      if (!this.mainImage || !this.thumbs.length) return;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.switchImage(i));
      });

      // Initialize swipe gallery for mobile
      this.initSwipe();
    },

    switchImage(index) {
      this.currentIndex = index;

      // Update main image
      const thumbImg = this.thumbs[index].querySelector('img');
      if (thumbImg && this.mainImage) {
        this.mainImage.src = thumbImg.dataset.fullSrc || thumbImg.src;
        this.mainImage.alt = thumbImg.alt;
      }

      // Update active thumb
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));

      // Update swipe dots
      $$('.product-gallery__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    },

    initSwipe() {
      const swipeContainer = $('.product-gallery__swipe');
      if (!swipeContainer) return;

      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      swipeContainer.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
      }, { passive: true });

      swipeContainer.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
      }, { passive: true });

      swipeContainer.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;

        const diff = startX - currentX;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
          if (diff > 0 && this.currentIndex < this.thumbs.length - 1) {
            this.switchImage(this.currentIndex + 1);
          } else if (diff < 0 && this.currentIndex > 0) {
            this.switchImage(this.currentIndex - 1);
          }
        }
      });

      // Dots navigation
      $$('.product-gallery__dot').forEach((dot, i) => {
        dot.addEventListener('click', () => this.switchImage(i));
      });
    }
  };

  /* ==========================================================================
     6. QUANTITY SELECTOR
     ========================================================================== */

  const QuantitySelector = {
    init() {
      $$('.quantity-selector').forEach(selector => {
        const minus = selector.querySelector('.quantity-selector__btn--minus, [data-action="decrease"]');
        const plus = selector.querySelector('.quantity-selector__btn--plus, [data-action="increase"]');
        const input = selector.querySelector('.quantity-selector__input, input[type="number"]');

        if (!input) return;

        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 9999;

        if (minus) {
          minus.addEventListener('click', () => {
            const val = parseInt(input.value) || min;
            if (val > min) {
              input.value = val - 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        if (plus) {
          plus.addEventListener('click', () => {
            const val = parseInt(input.value) || min;
            if (val < max) {
              input.value = val + 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        input.addEventListener('change', () => {
          let val = parseInt(input.value) || min;
          val = Math.max(min, Math.min(max, val));
          input.value = val;
          if (minus) minus.disabled = val <= min;
          if (plus) plus.disabled = val >= max;
        });

        // Trigger initial state
        input.dispatchEvent(new Event('change'));
      });
    }
  };

  /* ==========================================================================
     7. ADD TO CART (AJAX) + CART DRAWER
     ========================================================================== */

  const Cart = {
    drawer: null,
    backdrop: null,
    isOpen: false,

    init() {
      this.drawer = $('.cart-drawer');
      this.backdrop = $('.cart-drawer__backdrop');

      // Open cart drawer
      $$('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      });

      // Close cart drawer
      const closeBtn = $('.cart-drawer__close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      // Add to cart forms
      $$('form[data-add-to-cart]').forEach(form => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.addToCart(form);
        });
      });

      // Quick add buttons
      $$('[data-quick-add]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          if (variantId) this.addItem(variantId, 1);
        });
      });

      // Cart item quantity changes
      this.bindCartItemEvents();
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    open() {
      this.isOpen = true;
      if (this.drawer) this.drawer.classList.add('cart-drawer--open');
      if (this.backdrop) this.backdrop.classList.add('is-visible');
      document.documentElement.classList.add('cart-drawer--open');
      if (this.drawer) trapFocus(this.drawer);
    },

    close() {
      this.isOpen = false;
      if (this.drawer) this.drawer.classList.remove('cart-drawer--open');
      if (this.backdrop) this.backdrop.classList.remove('is-visible');
      document.documentElement.classList.remove('cart-drawer--open');
    },

    async addToCart(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('btn--loading');
        submitBtn.disabled = true;
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        const item = await response.json();
        await this.refreshCart();
        this.open();
        Toast.show({
          type: 'success',
          title: 'Added to cart',
          message: `${item.title} has been added to your cart.`
        });
      } catch (err) {
        console.error('Add to cart error:', err);
        Toast.show({
          type: 'error',
          title: 'Error',
          message: 'Could not add item to cart. Please try again.'
        });
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('btn--loading');
          submitBtn.disabled = false;
        }
      }
    },

    async addItem(variantId, quantity = 1) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });

        if (!response.ok) throw new Error('Failed to add item');

        await this.refreshCart();
        this.open();
        Toast.show({
          type: 'success',
          title: 'Added to cart',
          message: 'Item has been added to your cart.'
        });
      } catch (err) {
        console.error('Add item error:', err);
        Toast.show({
          type: 'error',
          title: 'Error',
          message: 'Could not add item to cart.'
        });
      }
    },

    async updateItem(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error('Failed to update cart');

        await this.refreshCart();
      } catch (err) {
        console.error('Update cart error:', err);
        Toast.show({
          type: 'error',
          title: 'Error',
          message: 'Could not update cart.'
        });
      }
    },

    async refreshCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.updateCartUI(cart);
        this.updateCartCount(cart.item_count);
      } catch (err) {
        console.error('Refresh cart error:', err);
      }
    },

    updateCartCount(count) {
      $$('.header__cart-count, [data-cart-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    },

    updateCartUI(cart) {
      const body = $('.cart-drawer__body');
      const footer = $('.cart-drawer__footer');
      const empty = $('.cart-drawer__empty');

      if (!body) return;

      if (cart.item_count === 0) {
        if (empty) empty.style.display = 'flex';
        if (footer) footer.style.display = 'none';
        // Clear items
        $$('.cart-item', body).forEach(item => item.remove());
        return;
      }

      if (empty) empty.style.display = 'none';
      if (footer) footer.style.display = '';

      // Update subtotal
      const subtotalEl = $('.cart-drawer__subtotal-value');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      // Update free shipping bar
      const freeShippingThreshold = 15000; // $150 in cents
      const freeShipFill = $('.free-shipping-bar__fill');
      const freeShipText = $('.free-shipping-bar__text');
      if (freeShipFill) {
        const pct = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);
        freeShipFill.style.width = pct + '%';
      }
      if (freeShipText) {
        if (cart.total_price >= freeShippingThreshold) {
          freeShipText.innerHTML = '🎉 You qualify for <strong>free shipping!</strong>';
        } else {
          const remaining = formatMoney(freeShippingThreshold - cart.total_price);
          freeShipText.innerHTML = `Spend <strong>${remaining}</strong> more for free shipping`;
        }
      }

      this.bindCartItemEvents();
    },

    bindCartItemEvents() {
      // Cart item quantity buttons
      $$('.cart-item__quantity button').forEach(btn => {
        btn.addEventListener('click', () => {
          const item = btn.closest('.cart-item');
          const key = item?.dataset.key;
          const input = item?.querySelector('input');
          if (!key || !input) return;

          let qty = parseInt(input.value) || 1;
          if (btn.dataset.action === 'decrease') qty = Math.max(0, qty - 1);
          else qty += 1;

          input.value = qty;
          this.updateItem(key, qty);
        });
      });

      // Remove buttons
      $$('.cart-item__remove').forEach(btn => {
        btn.addEventListener('click', () => {
          const item = btn.closest('.cart-item');
          const key = item?.dataset.key;
          if (key) this.updateItem(key, 0);
        });
      });
    }
  };

  /* ==========================================================================
     8. ANNOUNCEMENT BAR ROTATION
     ========================================================================== */

  const AnnouncementBar = {
    messages: [],
    currentIndex: 0,
    interval: null,
    delay: 5000,

    init() {
      this.messages = $$('.announcement-bar__message');
      if (this.messages.length <= 1) {
        // Single message — just show it
        if (this.messages[0]) this.messages[0].classList.add('is-active');
        return;
      }

      // Show first
      this.messages[0].classList.add('is-active');

      // Start rotation
      this.interval = setInterval(() => this.next(), this.delay);

      // Close button
      const closeBtn = $('.announcement-bar__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          const bar = $('.announcement-bar');
          if (bar) {
            bar.style.display = 'none';
            document.documentElement.style.setProperty('--announcement-height', '0px');
            const header = $('.site-header');
            if (header) header.classList.remove('has-announcement');
          }
          clearInterval(this.interval);
        });
      }
    },

    next() {
      this.messages[this.currentIndex].classList.remove('is-active');
      this.currentIndex = (this.currentIndex + 1) % this.messages.length;
      this.messages[this.currentIndex].classList.add('is-active');
    }
  };

  /* ==========================================================================
     9. FAQ ACCORDION
     ========================================================================== */

  const Accordion = {
    init() {
      // FAQ items
      $$('.faq-item__question').forEach(btn => {
        btn.addEventListener('click', () => {
          const item = btn.closest('.faq-item');
          if (!item) return;

          const wasOpen = item.classList.contains('is-open');

          // Close all siblings (optional — remove for multi-open)
          const parent = item.parentElement;
          if (parent) {
            $$('.faq-item.is-open', parent).forEach(open => {
              if (open !== item) open.classList.remove('is-open');
            });
          }

          item.classList.toggle('is-open', !wasOpen);
        });
      });

      // Product accordion
      $$('.accordion-item__header').forEach(header => {
        header.addEventListener('click', () => {
          const item = header.closest('.accordion-item');
          if (!item) return;
          item.classList.toggle('is-open');
        });
      });

      // Filter group collapse
      $$('.filter-group__header').forEach(header => {
        header.addEventListener('click', () => {
          const group = header.closest('.filter-group');
          if (group) group.classList.toggle('is-collapsed');
        });
      });
    }
  };

  /* ==========================================================================
     10. SCROLL-TRIGGERED ANIMATIONS (IntersectionObserver)
     ========================================================================== */

  const ScrollAnimations = {
    init() {
      const animatedEls = $$('.fade-in, .fade-in-left, .fade-in-right, .scale-in, .stagger-children, .hero');

      if (!animatedEls.length) return;

      if (!('IntersectionObserver' in window)) {
        // Fallback: show everything
        animatedEls.forEach(el => el.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
      });

      animatedEls.forEach(el => observer.observe(el));
    }
  };

  /* ==========================================================================
     11. SEARCH OVERLAY
     ========================================================================== */

  const SearchOverlay = {
    overlay: null,
    input: null,
    body: null,
    debounceTimer: null,

    init() {
      this.overlay = $('.search-overlay');
      this.input = $('.search-overlay__input');
      this.body = $('.search-overlay__body');

      if (!this.overlay) return;

      // Toggle triggers
      $$('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close
      const closeBtn = $('.search-overlay__close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      // Close on backdrop click
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('search-overlay--open')) {
          this.close();
        }
      });

      // Keyboard shortcut: Cmd/Ctrl + K
      document.addEventListener('keydown', (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          this.toggle();
        }
      });

      // Predictive search
      if (this.input) {
        this.input.addEventListener('input', debounce(() => this.search(), 300));
      }
    },

    toggle() {
      this.overlay?.classList.contains('search-overlay--open') ? this.close() : this.open();
    },

    open() {
      if (!this.overlay) return;
      this.overlay.classList.add('search-overlay--open');
      document.documentElement.classList.add('search-overlay--open');
      setTimeout(() => this.input?.focus(), 100);
      if (this.overlay) trapFocus(this.overlay);
    },

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('search-overlay--open');
      document.documentElement.classList.remove('search-overlay--open');
      if (this.input) this.input.value = '';
    },

    async search() {
      const query = this.input?.value?.trim();
      if (!query || query.length < 2) {
        this.showDefaultContent();
        return;
      }

      // Show loading
      if (this.body) {
        this.body.innerHTML = '<div class="search-loading"><div class="search-loading__spinner"></div></div>';
      }

      try {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6`
        );
        const data = await response.json();
        this.renderResults(data, query);
      } catch (err) {
        console.error('Search error:', err);
        if (this.body) {
          this.body.innerHTML = '<p class="text-center text-muted py-8">Something went wrong. Please try again.</p>';
        }
      }
    },

    renderResults(data, query) {
      if (!this.body) return;

      const products = data.resources?.results?.products || [];
      const collections = data.resources?.results?.collections || [];

      if (!products.length && !collections.length) {
        this.body.innerHTML = `
          <div class="text-center py-8">
            <p class="text-muted mb-4">No results found for "<strong>${query}</strong>"</p>
            <p class="text-sm text-muted">Try a different search term or browse our categories.</p>
          </div>`;
        return;
      }

      let html = '';

      if (products.length) {
        html += '<div class="search-results__section">';
        html += '<h4 class="search-results__title">Products</h4>';
        products.forEach(p => {
          const img = p.image || p.featured_image?.url || '';
          html += `
            <a href="${p.url}" class="search-result">
              <div class="search-result__image">
                ${img ? `<img src="${img}" alt="${p.title}" loading="lazy">` : ''}
              </div>
              <div class="search-result__info">
                <div class="search-result__name">${p.title}</div>
                <div class="search-result__price">${formatMoney(p.price)}</div>
              </div>
            </a>`;
        });
        html += '</div>';
      }

      if (collections.length) {
        html += '<div class="search-results__section">';
        html += '<h4 class="search-results__title">Collections</h4>';
        collections.forEach(c => {
          html += `
            <a href="${c.url}" class="search-result">
              <div class="search-result__info">
                <div class="search-result__name">${c.title}</div>
              </div>
            </a>`;
        });
        html += '</div>';
      }

      // View all link
      html += `
        <div class="text-center mt-4 mb-2">
          <a href="/search?q=${encodeURIComponent(query)}" class="btn btn--sm btn--outline">View all results</a>
        </div>`;

      this.body.innerHTML = html;
    },

    showDefaultContent() {
      if (!this.body) return;
      // Show suggestions or popular searches — keep existing content
    }
  };

  /* ==========================================================================
     12. COLLECTION VIEW TOGGLE (Grid/List)
     ========================================================================== */

  const ViewToggle = {
    init() {
      $$('.view-toggle__btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const view = btn.dataset.view;
          const grid = $('.products-grid');
          if (!grid || !view) return;

          // Update buttons
          $$('.view-toggle__btn').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Update grid
          if (view === 'list') {
            grid.classList.remove('products-grid');
            grid.classList.add('products-list');
          } else {
            grid.classList.remove('products-list');
            grid.classList.add('products-grid');
          }

          // Save preference
          try { localStorage.setItem('zen-view', view); } catch (e) {}
        });
      });

      // Restore preference
      try {
        const saved = localStorage.getItem('zen-view');
        if (saved) {
          const btn = $(`.view-toggle__btn[data-view="${saved}"]`);
          if (btn) btn.click();
        }
      } catch (e) {}
    }
  };

  /* ==========================================================================
     13. TOAST NOTIFICATION SYSTEM
     ========================================================================== */

  const Toast = {
    container: null,

    init() {
      // Create container if not exists
      if (!$('.toast-container')) {
        const div = document.createElement('div');
        div.className = 'toast-container';
        document.body.appendChild(div);
      }
      this.container = $('.toast-container');
    },

    show({ type = 'success', title = '', message = '', duration = 4000 }) {
      if (!this.container) this.init();

      const icons = {
        success: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
        error: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
        warning: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
        info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
      };

      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.innerHTML = `
        <span class="toast__icon">${icons[type] || icons.info}</span>
        <div class="toast__content">
          ${title ? `<div class="toast__title">${title}</div>` : ''}
          ${message ? `<div class="toast__message">${message}</div>` : ''}
        </div>
        <button class="toast__close" aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div class="toast__progress" style="animation-duration:${duration}ms"></div>
      `;

      // Close button
      toast.querySelector('.toast__close')?.addEventListener('click', () => this.dismiss(toast));

      this.container.appendChild(toast);

      // Animate in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => toast.classList.add('is-visible'));
      });

      // Auto dismiss
      if (duration > 0) {
        setTimeout(() => this.dismiss(toast), duration);
      }

      return toast;
    },

    dismiss(toast) {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }
  };

  /* ==========================================================================
     14. SMOOTH SCROLL FOR ANCHOR LINKS
     ========================================================================== */

  const SmoothScroll = {
    init() {
      $$('a[href^="#"]').forEach(link => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');
          if (href === '#' || !href) return;

          const target = document.querySelector(href);
          if (!target) return;

          e.preventDefault();
          const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;

          const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 20;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });
    }
  };

  /* ==========================================================================
     15. BACK TO TOP BUTTON
     ========================================================================== */

  const BackToTop = {
    btn: null,

    init() {
      this.btn = $('.back-to-top');
      if (!this.btn) return;

      window.addEventListener('scroll', throttle(() => {
        this.btn.classList.toggle('is-visible', window.scrollY > 600);
      }, 100), { passive: true });

      this.btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  /* ==========================================================================
     16. NEWSLETTER FORM AJAX
     ========================================================================== */

  const Newsletter = {
    init() {
      $$('.newsletter-form, .footer-newsletter__form').forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const input = form.querySelector('input[type="email"]');
          const btn = form.querySelector('button[type="submit"], .newsletter-form__btn');
          const email = input?.value?.trim();

          if (!email || !this.isValidEmail(email)) {
            Toast.show({ type: 'error', title: 'Invalid email', message: 'Please enter a valid email address.' });
            return;
          }

          if (btn) {
            btn.classList.add('btn--loading');
            btn.disabled = true;
          }

          try {
            // Shopify newsletter subscribe via customer API
            const formData = new FormData();
            formData.append('form_type', 'customer');
            formData.append('utf8', '✓');
            formData.append('customer[email]', email);
            formData.append('customer[tags]', 'newsletter');

            await fetch('/contact#contact_form', {
              method: 'POST',
              body: formData,
              headers: { 'Accept': 'application/json' }
            });

            // Show success
            if (input) input.value = '';
            const success = form.parentElement?.querySelector('.newsletter-success');
            if (success) success.classList.add('is-visible');

            Toast.show({ type: 'success', title: 'Subscribed!', message: 'Thanks for joining the ZenPacks community.' });
          } catch (err) {
            console.error('Newsletter error:', err);
            Toast.show({ type: 'error', title: 'Error', message: 'Something went wrong. Please try again.' });
          } finally {
            if (btn) {
              btn.classList.remove('btn--loading');
              btn.disabled = false;
            }
          }
        });
      });
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
  };

  /* ==========================================================================
     17. PRODUCT VARIANT SELECTOR
     ========================================================================== */

  const VariantSelector = {
    init() {
      $$('.variant-option').forEach(option => {
        option.addEventListener('click', () => {
          const selector = option.closest('.variant-selector');
          if (!selector) return;

          // Update active state
          $$('.variant-option', selector).forEach(o => o.classList.remove('is-active'));
          option.classList.add('is-active');

          // Update label
          const label = selector.querySelector('.variant-selector__label span');
          if (label) label.textContent = option.textContent.trim();

          // Update hidden select if present
          const select = selector.querySelector('select');
          if (select) {
            select.value = option.dataset.value || option.textContent.trim();
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // Update product info (price, image, availability)
          this.updateProduct(option.dataset.variantId);
        });
      });

      // Native select change handler
      $$('.variant-selector select').forEach(select => {
        select.addEventListener('change', () => {
          const option = select.options[select.selectedIndex];
          if (option) this.updateProduct(option.dataset.variantId);
        });
      });
    },

    async updateProduct(variantId) {
      if (!variantId) return;

      try {
        // Update URL without reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variantId);
        history.replaceState(null, '', url);

        // Fetch variant data
        const productHandle = $('[data-product-handle]')?.dataset.productHandle;
        if (!productHandle) return;

        const response = await fetch(`/products/${productHandle}.js`);
        const product = await response.json();
        const variant = product.variants.find(v => v.id == variantId);

        if (!variant) return;

        // Update price
        const priceEl = $('.product-info__price-current');
        const compareEl = $('.product-info__price-compare');
        const saveEl = $('.product-info__price-save');

        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
            if (saveEl) {
              const savePercent = Math.round((1 - variant.price / variant.compare_at_price) * 100);
              saveEl.textContent = `Save ${savePercent}%`;
              saveEl.style.display = '';
            }
          } else {
            compareEl.style.display = 'none';
            if (saveEl) saveEl.style.display = 'none';
          }
        }

        // Update image
        if (variant.featured_image) {
          const mainImg = $('.product-gallery__main img');
          if (mainImg) mainImg.src = variant.featured_image.src;
        }

        // Update availability
        const addBtn = $('[data-add-to-cart] [type="submit"]');
        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = 'Add to Cart';
          } else {
            addBtn.disabled = true;
            addBtn.textContent = 'Sold Out';
          }
        }

        // Update hidden variant ID input
        const hiddenInput = $('input[name="id"]');
        if (hiddenInput) hiddenInput.value = variantId;

      } catch (err) {
        console.error('Variant update error:', err);
      }
    }
  };

  /* ==========================================================================
     18. LAZY IMAGE LOADING WITH BLUR-UP
     ========================================================================== */

  const LazyImages = {
    init() {
      const images = $$('img[data-src], img[loading="lazy"]');

      if (!images.length) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadImage(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '200px 0px'
        });

        images.forEach(img => {
          if (img.dataset.src) {
            img.classList.add('is-loading');
            observer.observe(img);
          }
        });
      } else {
        // Fallback: load all immediately
        images.forEach(img => this.loadImage(img));
      }
    },

    loadImage(img) {
      if (!img.dataset.src) return;

      const src = img.dataset.src;
      const srcset = img.dataset.srcset;

      const temp = new Image();
      temp.onload = () => {
        img.src = src;
        if (srcset) img.srcset = srcset;
        img.classList.remove('is-loading');
        img.classList.add('is-loaded');

        // Trigger blur-up parent
        const parent = img.closest('.lazy-image');
        if (parent) parent.classList.add('is-loaded');
      };
      temp.src = src;
    }
  };

  /* ==========================================================================
     19. TESTIMONIALS SLIDER
     ========================================================================== */

  const TestimonialsSlider = {
    track: null,
    cards: [],
    currentIndex: 0,
    slidesPerView: 3,

    init() {
      this.track = $('.testimonials-track');
      this.cards = $$('.testimonial-card');

      if (!this.track || this.cards.length <= 1) return;

      this.updateSlidesPerView();

      // Navigation
      const prevBtn = $('.slider-nav__btn--prev, [data-slider-prev]');
      const nextBtn = $('.slider-nav__btn--next, [data-slider-next]');

      if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
      if (nextBtn) nextBtn.addEventListener('click', () => this.next());

      // Dots
      $$('.slider-dot').forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });

      // Resize handler
      window.addEventListener('resize', debounce(() => {
        this.updateSlidesPerView();
        this.goTo(this.currentIndex);
      }, 200));

      // Touch/swipe
      let startX = 0;
      this.track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          diff > 0 ? this.next() : this.prev();
        }
      });
    },

    updateSlidesPerView() {
      const w = window.innerWidth;
      if (w < 768) this.slidesPerView = 1;
      else if (w < 1024) this.slidesPerView = 2;
      else this.slidesPerView = 3;
    },

    prev() {
      this.goTo(Math.max(0, this.currentIndex - 1));
    },

    next() {
      const maxIndex = Math.max(0, this.cards.length - this.slidesPerView);
      this.goTo(Math.min(maxIndex, this.currentIndex + 1));
    },

    goTo(index) {
      const maxIndex = Math.max(0, this.cards.length - this.slidesPerView);
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));

      const pct = (this.currentIndex / this.cards.length) * 100;
      if (this.track) {
        this.track.style.transform = `translateX(-${pct}%)`;
      }

      // Update dots
      $$('.slider-dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });

      // Update nav buttons
      const prevBtn = $('.slider-nav__btn--prev, [data-slider-prev]');
      const nextBtn = $('.slider-nav__btn--next, [data-slider-next]');
      if (prevBtn) prevBtn.disabled = this.currentIndex === 0;
      if (nextBtn) nextBtn.disabled = this.currentIndex >= maxIndex;
    }
  };

  /* ==========================================================================
     20. COLLECTION SORT
     ========================================================================== */

  const CollectionSort = {
    init() {
      const sortSelect = $('#sort-by, [data-sort-select]');
      if (!sortSelect) return;

      sortSelect.addEventListener('change', () => {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location = url.toString();
      });
    }
  };

  /* ==========================================================================
     INITIALIZATION
     ========================================================================== */

  function init() {
    StickyHeader.init();
    MobileMenu.init();
    MegaMenu.init();
    ProductGallery.init();
    QuantitySelector.init();
    Cart.init();
    AnnouncementBar.init();
    Accordion.init();
    ScrollAnimations.init();
    SearchOverlay.init();
    ViewToggle.init();
    Toast.init();
    SmoothScroll.init();
    BackToTop.init();
    Newsletter.init();
    VariantSelector.init();
    LazyImages.init();
    TestimonialsSlider.init();
    CollectionSort.init();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose Toast globally for external use
  window.ZenPacks = window.ZenPacks || {};
  window.ZenPacks.Toast = Toast;
  window.ZenPacks.Cart = Cart;

})();
